import styled, { css } from 'styled-components'
import { Trans } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'next-i18next'

import AffirmPromotional, {
  AffirmPromotionalType,
  AffirmPromotionalPageType,
} from 'productTemplate/components/AffirmPromotional'
import {
  AfterPayPromotional,
  AfterPayPromotionalType,
} from 'productTemplate/components/AfterPayPromotional'
import PaymentOption from 'checkout/enums/PaymentOption'
import PaymentType from 'shared/enums/PaymentType'
import RadioButton from 'checkout/components/RadioButton'
import RadioGroup from 'checkout/components/RadioGroup'
import Spinner from 'shared/components/Spinner'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { BillingInfo } from 'shared/types/BillingInfo'
import { Button } from 'shared/components/Button'
import { CardBrand } from 'shared/components/Icons/PaymentIcons/CardIcon/CardIcon'
import { CardIcon, AfterPayIcon, AffirmIcon, PayPalIcon, KlarnaIcon } from 'shared/components/Icons'
import { CheckoutQA } from 'shared/dataAttributes'
import { FCLink } from 'shared/components/Links'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { KlarnaPromotional } from 'checkout/components/PaymentMethodPanel/KlarnaPromotional'
import { colors, fonts } from 'shared/lib'
import { paymentOptionDisplayMap } from 'checkout/utils/paymentOptionDisplayMap'
import { sizes } from 'shared/lib/media'
import { t } from 'localization'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useGetAddressQuery } from 'api/addressesApi'
import { useGetBillingInfosQuery } from 'api/billingInfosApi'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { useGetPaymentAvailabilityQuery } from 'api/paymentAvailabilityApi'
import { ThirdPartyPaymentOption } from 'checkout/panels/PaymentMethod/ThirdPartyPaymentOption'
import { NewCardPaymentOption } from 'checkout/panels/PaymentMethod/NewCardPaymentOption'
import { NewCardForm } from 'checkout/panels/PaymentMethod/NewCardForm'
import { SavedCardPaymentOption } from 'checkout/panels/PaymentMethod/SavedCardPaymentOption'
import { UpdateCurrencyModal } from 'checkout/panels/PaymentMethod/UpdateCurrencyModal'

const TP = 'checkout.panels.PaymentMethod.savedPaymentMethodsForm'

type SavedPaymentMethodsFormProps = {
  currentBillingInfo?: BillingInfo
}

export const SavedPaymentMethodsForm = ({ currentBillingInfo }: SavedPaymentMethodsFormProps) => {
  const {
    panels,
    order,
    selectAffirmRTK,
    selectAfterPayRTK,
    selectCreditCardRTK,
    selectKlarnaRTK,
    selectPayPalRTK,
    updatePanel,
  } = useCheckoutContext()

  const { data: shippingAddress } = useGetAddressQuery(order?.addressId)
  const { data, isFetching } = useGetBillingInfosQuery(null, { refetchOnMountOrArgChange: true })
  const creditCards = data?.billingInfos || []

  const tv = useTranslation()
  const isMobile = useMediaQuery({ maxWidth: sizes.small })
  const { country: selectedCountryCode, currencyCode: selectedCurrency } =
    useShoppingRegionContext()
  const defaultPaymentMethod = getDefaultPaymentMethod(currentBillingInfo)
  const [selectedMethod, setSelectedMethod] = useState(defaultPaymentMethod)
  const [updateCurrencyModalState, setUpdateCurrencyModalState] = useState(
    defaultUpdateCurrencyModalState,
  )
  const { loading } = panels.paymentMethod
  const orderRequiresPayment = !!order?.localizedFinalPriceCents?.amount
  const klarnaPriceEligible =
    order &&
    order?.localizedFinalPriceCents?.amount > 3500 &&
    order?.localizedFinalPriceCents?.amount < 1000000
  const isExposedPaymentMethods = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EXPOSED_PAYMENT_METHODS)
  const isKlarnaEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_KLARNA)
  const isPayPalv2Enabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PAYPAL_TWO)
  const isAfterPayExpressEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_AFTERPAY)
  const isExpressAffirmEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_AFFIRM)
  const isAffirmLocaleValidationEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_AFFIRM_WITH_LOCALE)
  const isPaymentAvailabilityServiceEnabled = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_PAYMENT_AVAILABILITY_SERVICE,
  )
  const isOffersEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_OFFERS)
  const isUsd = selectedCurrency === 'USD'
  const isWantOrder = isOffersEnabled && order?.wantId

  const { data: paymentAvailability, isSuccess: isSuccessPaymentAvailability } =
    useGetPaymentAvailabilityQuery(
      {
        country_code: selectedCountryCode,
        currency: selectedCurrency,
        final_price_cents: order?.localizedFinalPriceCents?.amount,
        // orderTypes: 1 is regular purchase, 2 is offer, 3 is drop
        order_type: 1,
        goat_storage: false,
        shipping_region: shippingAddress?.countryCode,
      },
      {
        // When skip is true, it doesnt make server call.
        skip: !isPaymentAvailabilityServiceEnabled,
      },
    )

  const showKlarna = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.klarna && !isWantOrder
    : isKlarnaEnabled && klarnaPriceEligible && orderRequiresPayment && isUsd && !isWantOrder
  const showAfterPay = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.afterpay && !isWantOrder
    : order?.isAfterpayEligible && isUsd && !isWantOrder
  const showAffirm = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.affirm && !isWantOrder
    : isExpressAffirmEnabled && isUsd && !isWantOrder
  const showPayPal = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.paypal
    : isPayPalv2Enabled && orderRequiresPayment && isUsd

  const isThirdPartyPayment = isThirdPartyPaymentMethod(selectedMethod)

  const handleChangePaymentMethod = (paymentMethod: PaymentOption | number) => {
    if (!isUsd && isThirdPartyPaymentMethod(paymentMethod)) {
      setUpdateCurrencyModalState({ isOpen: true, selectedPaymentMethod: paymentMethod })
      return
    }
    setSelectedMethod(paymentMethod)
    const paymentAttributes = (() => {
      switch (paymentMethod) {
        case PaymentOption.Affirm:
          return { third_party_payment_method: 'Affirm' }
        case PaymentOption.AfterPay:
          return { third_party_payment_method: 'Afterpay' }
        case PaymentOption.Klarna:
          return { third_party_payment_method: 'Klarna' }
        case PaymentOption.PayPalv2:
          return { third_party_payment_method: 'PayPal' }
        case PaymentOption.NewCard:
          return { is_add_new_card: true }
        default:
          return { billing_info_id: paymentMethod }
      }
    })()
    sendTrackingEvent('PAYMENT_SELECTION_TAP', {
      order_number: order?.number,
      third_party_payment_method: 'NULL',
      is_add_new_card: false,
      billing_info_id: 'NULL',
      ...paymentAttributes,
    })
  }

  const handleContinueClick = async () => {
    if (typeof selectedMethod === 'number') {
      selectCreditCardRTK(selectedMethod)
    }
    if (selectedMethod === PaymentOption.NewCard) {
      updatePanel('paymentMethod', { view: 'new' })
    }
    if (selectedMethod === PaymentOption.Affirm) {
      selectAffirmRTK(paymentAvailability?.affirm?.internalName, shippingAddress)
    }
    if (selectedMethod === PaymentOption.PayPalv2) {
      selectPayPalRTK()
    }
    if (selectedMethod === PaymentOption.AfterPay) {
      selectAfterPayRTK()
    }
    if (selectedMethod === PaymentOption.Klarna) {
      selectKlarnaRTK()
    }
  }

  const trackUpdateCurrency = (event: string) => {
    sendTrackingEvent(event, {
      old_currency: selectedCurrency,
      new_currency: 'USD',
      payment_processor: paymentOptionDisplayMap.get(
        updateCurrencyModalState.selectedPaymentMethod,
      ),
    })
  }

  const continueButtonText = (() => {
    switch (true) {
      case selectedMethod === PaymentOption.Klarna:
        return t(`${TP}.placeOrderWithKlarna`, 'Place order with Klarna')
      case selectedMethod === PaymentOption.PayPalv2:
        return t(`${TP}.placeOrderWithPayPal`, 'Place Order with PayPal')
      case selectedMethod === PaymentOption.Affirm && isExpressAffirmEnabled:
        return t(`${TP}.placeOrderWithAffirm`, 'Place Order with Affirm')
      case selectedMethod === PaymentOption.AfterPay && isAfterPayExpressEnabled:
        return t(`${TP}.placeOrderWithAfterPay`, 'Place Order with Afterpay')
      default:
        return t(`${TP}.continue`, 'Continue')
    }
  })()

  const continueButtonSubHeader = (() => {
    switch (true) {
      case selectedMethod === PaymentOption.Klarna:
        return t(
          `${TP}.redirectedToKlarna`,
          'You will be redirected to Klarna to complete your purchase securely.',
        )
      case selectedMethod === PaymentOption.PayPalv2:
        return t(
          `${TP}.redirectedToPayPal`,
          'You will be redirected to PayPal to complete your purchase securely.',
        )
      case selectedMethod === PaymentOption.Affirm && isExpressAffirmEnabled:
        return t(
          `${TP}.redirectedToAffirm`,
          'You will be redirected to Affirm to provide your payment information.',
        )
      case selectedMethod === PaymentOption.AfterPay && isAfterPayExpressEnabled:
        return t(
          `${TP}.redirectedToAfterPay`,
          'You will be redirected to Afterpay to provide your payment information.',
        )
      default:
        return t(`${TP}.redirectedToOthers`, {
          defaultValue:
            'You will be redirected to {paymentOption} to provide your payment information. To complete your order you must return to Flight Club.',
          paymentOption: paymentOptionDisplayMap.get(selectedMethod as PaymentOption),
        })
    }
  })()

  useEffect(() => {
    if (isAffirmLocaleValidationEnabled && isSuccessPaymentAvailability && showAffirm) {
      // flightclub.com only supports US or Japan locales; Since Affirm can only be used with USD orders
      // hardcode countryCode and locale to Affirm's US values
      const countryCode = 'USA'
      const locale = 'en_US'

      // https://docs.affirm.com/developers/v1.0-Global-Integration/docs/global-affirm-integration-guide
      window._affirm_config = {
        country_code: countryCode,
        locale,
        public_api_key: paymentAvailability?.affirm?.publicKey,
        script: paymentAvailability?.affirm?.scriptUrl,
      }

      /* eslint-disable prefer-rest-params */
      ;(function (m, g, n, d, a, e, h, c) {
        const b = m[n] || {},
          k = document.createElement(e),
          p = document.getElementsByTagName(e)[0],
          l = function (a, b, c) {
            return function () {
              a[b]._.push([c, arguments])
            }
          }
        b[d] = l(b, d, 'set')
        const f = b[d]
        b[a] = {}
        b[a]._ = []
        f._ = []
        b._ = []
        b[a][h] = l(b, a, h)
        b[c] = function () {
          b._.push([h, arguments])
        }
        a = 0
        for (
          c = 'set add save post open empty reset on off trigger ready setProduct'.split(' ');
          a < c.length;
          a++
        )
          f[c[a]] = l(b, d, c[a])
        a = 0
        for (c = ['get', 'token', 'url', 'items']; a < c.length; a++) f[c[a]] = function () {}
        k.async = !0
        k.src = g[e]
        p.parentNode.insertBefore(k, p)
        delete g[e]
        f(g)
        m[n] = b
      })(window, _affirm_config, 'affirm', 'checkout', 'ui', 'script', 'ready', 'jsReady')
      /* eslint-enable prefer-rest-params */
    }
  }, [isAffirmLocaleValidationEnabled, isSuccessPaymentAvailability, showAffirm])

  return (
    <Spinner
      showSpinner={isFetching || loading}
      text={t(`${TP}.spinnerText`, 'Updating payment info...')}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
    >
      <RadioGroup
        data-testid="payment-methods"
        qaAttr={CheckoutQA.PaymentMethodRadioGroup}
        name="paymentMethods"
      >
        {isExposedPaymentMethods ? (
          <>
            {!!creditCards?.length &&
              creditCards.map(({ cardBrand, cardExpMonth, cardExpYear, id, last4Digits }) => (
                <RadioButton
                  checked={selectedMethod === id}
                  data-testid={`creditCardButton${id}`}
                  displayRadioButton
                  key={id}
                  onRadioSelect={() => handleChangePaymentMethod(id)}
                  qaAttr={CheckoutQA.CreditCardOption}
                  title={
                    <SavedCardPaymentOption
                      cardBrand={cardBrand}
                      cardExpMonth={cardExpMonth}
                      cardExpYear={cardExpYear}
                      last4Digits={last4Digits}
                    />
                  }
                  value={id.toString()}
                />
              ))}
            <NewCardRadioButton
              checked={selectedMethod === PaymentOption.NewCard}
              displayRadioButton
              isMobile={isMobile}
              onRadioSelect={() => handleChangePaymentMethod(PaymentOption.NewCard)}
              qaAttr={CheckoutQA.AddNewCardOption}
              tags={selectedMethod === PaymentOption.NewCard && <NewCardForm />}
              title={<NewCardPaymentOption />}
              value="newCard"
            />
            {showKlarna && (
              <RadioButton
                checked={selectedMethod === PaymentOption.Klarna}
                data-testid="klarnaOptionButton"
                displayRadioButton
                isMobile={isMobile}
                onRadioSelect={() => handleChangePaymentMethod(PaymentOption.Klarna)}
                qaAttr={CheckoutQA.KlarnaOption}
                title={
                  <ThirdPartyPaymentOption
                    paymentType={PaymentType.Klarna}
                    price={order?.localizedFinalPriceCents?.amount}
                  />
                }
                value="klarnaButton"
              />
            )}
            {showPayPal && (
              <RadioButton
                checked={selectedMethod === PaymentOption.PayPalv2}
                data-testid="paypalOptionButton"
                displayRadioButton
                isMobile={isMobile}
                onRadioSelect={() => handleChangePaymentMethod(PaymentOption.PayPalv2)}
                qaAttr={CheckoutQA.PayPalv2Option}
                title={<ThirdPartyPaymentOption paymentType={PaymentType.PayPalv2} />}
                value="paypalv2Button"
              />
            )}
            {showAffirm && (
              <RadioButton
                checked={selectedMethod === PaymentOption.Affirm}
                data-testid="affirmOptionButton"
                displayRadioButton
                isMobile={isMobile}
                onRadioSelect={() => handleChangePaymentMethod(PaymentOption.Affirm)}
                qaAttr={CheckoutQA.AffirmOption}
                title={
                  <ThirdPartyPaymentOption
                    paymentType={PaymentType.Affirm}
                    price={order?.localizedFinalPriceCents?.amount}
                  />
                }
                value="affirmButton"
              />
            )}
            {showAfterPay && (
              <RadioButton
                checked={selectedMethod === PaymentOption.AfterPay}
                data-testid="afterPayButton"
                displayRadioButton
                isMobile={isMobile}
                onRadioSelect={() => handleChangePaymentMethod(PaymentOption.AfterPay)}
                qaAttr={CheckoutQA.AfterPayOption}
                title={<ThirdPartyPaymentOption paymentType={PaymentType.AfterPay} />}
                value="afterPay"
              />
            )}
            {isThirdPartyPayment && (
              <RedirectNotice
                $withBoxStyles
                data-qa={CheckoutQA.PaymentRedirectNotice}
                data-testid="paymentRedirectNotice"
              >
                {continueButtonSubHeader}
                {isThirdPartyPayment && (
                  <TermsAndPrivacyText>
                    <Trans
                      i18nKey={`${TP}.TermsAndPrivacyText`}
                      t={tv.t}
                      defaults="By proceeding, you acknowledge that you have read and agree to the <0>Terms & Conditions</0> and the <1>Privacy Policy</1>."
                      components={[
                        <FCLink key="terms-conditions" href="/terms-conditions" newTab />,
                        <FCLink key="privacy" href="/privacy" newTab />,
                      ]}
                    />
                  </TermsAndPrivacyText>
                )}
              </RedirectNotice>
            )}
          </>
        ) : (
          <>
            {!!creditCards?.length &&
              creditCards.map(({ id, cardBrand, last4Digits }) => (
                <PaymentMethodRadio
                  qaAttr={CheckoutQA.CreditCardOption}
                  title={
                    <>
                      <CardIcon brand={cardBrand} />
                      <SavedCreditCardSpan>
                        {` ${cardBrand?.toUpperCase()} *${last4Digits}`}
                      </SavedCreditCardSpan>
                    </>
                  }
                  key={id}
                  value={id.toString()}
                  checked={id === selectedMethod}
                  onRadioSelect={() => setSelectedMethod(id)}
                  data-testid={`creditCardButton${id}`}
                />
              ))}
            <PaymentMethodRadio
              isMobile={isMobile}
              qaAttr={CheckoutQA.AddNewCardOption}
              title={t(`${TP}.addNewCard`, 'Add New Card')}
              value="newCard"
              checked={PaymentOption.NewCard === selectedMethod}
              onRadioSelect={() => setSelectedMethod(PaymentOption.NewCard)}
              tags={
                <div>
                  <StyledCardIcon brand={CardBrand.Visa} />
                  <StyledCardIcon brand={CardBrand.MasterCard} />
                  <StyledCardIcon brand={CardBrand.AmericanExpress} />
                </div>
              }
            />
            {(showKlarna || showAffirm || showPayPal || showAfterPay) && isUsd && <Divider />}
            {showKlarna && (
              <PaymentMethodRadio
                checked={PaymentOption.Klarna === selectedMethod}
                data-testid="klarnaOptionButton"
                isMobile={isMobile}
                onRadioSelect={() => setSelectedMethod(PaymentOption.Klarna)}
                qaAttr={CheckoutQA.KlarnaOption}
                tags={
                  <StyledPaymentSubTitle isMobile={isMobile}>
                    <KlarnaPromotional price={order?.localizedFinalPriceCents?.amount} />
                  </StyledPaymentSubTitle>
                }
                title={<KlarnaIcon />}
                value="klarnaButton"
              />
            )}
            {showAffirm && (
              <PaymentMethodRadio
                checked={PaymentOption.Affirm === selectedMethod}
                data-testid="affirmOptionButton"
                isMobile={isMobile}
                onRadioSelect={() => setSelectedMethod(PaymentOption.Affirm)}
                qaAttr={CheckoutQA.AffirmOption}
                tags={
                  <StyledPaymentSubTitle isMobile={isMobile}>
                    <StyledAffirmPromotional
                      amount={order?.localizedFinalPriceCents?.amount}
                      affirmType={AffirmPromotionalType.Text}
                      pageType={AffirmPromotionalPageType.Payment}
                    />
                  </StyledPaymentSubTitle>
                }
                title={<StyledAffirmIcon aria-label="affirm" />}
                value="affirmButton"
              />
            )}
            {showPayPal && (
              <PaymentMethodRadio
                checked={PaymentOption.PayPalv2 === selectedMethod}
                data-testid="paypalOptionButton"
                isMobile={isMobile}
                onRadioSelect={() => setSelectedMethod(PaymentOption.PayPalv2)}
                qaAttr={CheckoutQA.PayPalv2Option}
                title={<PayPalIcon />}
                value="paypalv2Button"
              />
            )}
            {showAfterPay && (
              <PaymentMethodRadio
                checked={PaymentOption.AfterPay === selectedMethod}
                data-testid="afterPayButton"
                isMobile={isMobile}
                onRadioSelect={() => setSelectedMethod(PaymentOption.AfterPay)}
                qaAttr={CheckoutQA.AfterPayOption}
                tags={
                  <StyledPaymentSubTitle isMobile={isMobile}>
                    <AfterPayPromotional type={AfterPayPromotionalType.Text} />
                  </StyledPaymentSubTitle>
                }
                title={<AfterPayIcon aria-label="afterPay" />}
                value="afterPay"
              />
            )}
          </>
        )}
      </RadioGroup>
      <ContinueButton
        $fill
        buttonType="primary1"
        dataTestid="continueButton"
        disabled={isExposedPaymentMethods && selectedMethod === PaymentOption.NewCard}
        onClick={handleContinueClick}
        qaAttr={CheckoutQA.PaymentMethodContinueButton}
      >
        {continueButtonText}
      </ContinueButton>
      {!isExposedPaymentMethods && isThirdPartyPayment && (
        <RedirectNotice
          data-qa={CheckoutQA.PaymentRedirectNotice}
          data-testid="paymentRedirectNotice"
        >
          {continueButtonSubHeader}
          {isThirdPartyPayment && (
            <TermsAndPrivacyText>
              <Trans
                i18nKey={`${TP}.TermsAndPrivacyText`}
                t={tv.t}
                defaults="By proceeding, you acknowledge that you have read and agree to the <0>Terms & Conditions</0> and the <1>Privacy Policy</1>."
                components={[
                  <FCLink key="terms-conditions" href="/terms-conditions" newTab />,
                  <FCLink key="privacy" href="/privacy" newTab />,
                ]}
              />
            </TermsAndPrivacyText>
          )}
        </RedirectNotice>
      )}
      {updateCurrencyModalState.isOpen && (
        <UpdateCurrencyModal
          isOpen={updateCurrencyModalState.isOpen}
          onClose={() => setUpdateCurrencyModalState(defaultUpdateCurrencyModalState)}
          onUpdateCurrency={() => setSelectedMethod(updateCurrencyModalState.selectedPaymentMethod)}
          selectedPaymentOption={updateCurrencyModalState.selectedPaymentMethod}
          trackUpdateCurrency={trackUpdateCurrency}
        />
      )}
    </Spinner>
  )
}

const isThirdPartyPaymentMethod = (paymentMethod: number | PaymentOption) => {
  return [
    PaymentOption.Affirm,
    PaymentOption.AfterPay,
    PaymentOption.Klarna,
    PaymentOption.PayPalv2,
  ].includes(paymentMethod)
}

const getDefaultPaymentMethod = (billingInfo?: BillingInfo) => {
  switch (billingInfo?.paymentType) {
    case PaymentType.PayPalv2:
      return PaymentOption.PayPalv2
    case PaymentType.AfterPay:
      return PaymentOption.AfterPay
    case PaymentType.Affirm:
      return PaymentOption.Affirm
    case PaymentType.Klarna:
      return PaymentOption.Klarna
    case PaymentType.CreditCard:
      return billingInfo?.id || PaymentOption.NewCard
    default:
      return PaymentOption.NewCard
  }
}

const defaultUpdateCurrencyModalState = {
  isOpen: false,
  selectedPaymentMethod: null,
}

const ContinueButton = styled(Button)`
  margin-top: 12px;
`
const Divider = styled.div`
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
  margin: 24px 0;
`
const NewCardRadioButton = styled(RadioButton)`
  ${/* sc-selector */ RadioButton.Header} {
    flex-direction: column;
  }
  ${/* sc-selector */ RadioButton.Radio} {
    align-self: flex-start;
    margin-top: 8px;
  }
`
const PaymentMethodRadio = styled(RadioButton)`
  height: 64px;
  padding: ${(props) => (props.isMobile ? '15px 20px' : '20px')};
  ${/* sc-selector */ RadioButton.RadioBox} {
    width: auto;
  }
`
const RedirectNotice = styled.p<{ $withBoxStyles?: boolean }>`
  ${({ $withBoxStyles }) =>
    $withBoxStyles
      ? css`
          border: 1px solid ${colors.FC2_LIGHTEST_GREY};
          background-color: ${colors.FC2_OFF_WHITE};
          margin: 24px 0 12px 0;
          padding: 24px;
        `
      : css`
          margin-top: 20px;
          ${fonts.BODY_TEXT}
        `};
`
const SavedCreditCardSpan = styled.span`
  vertical-align: super;
`
const StyledAffirmIcon = styled(AffirmIcon)`
  flex-shrink: 0;
`
const StyledAffirmPromotional = styled(AffirmPromotional)`
  ${fonts.BODY_TEXT}
`
const StyledCardIcon = styled(CardIcon)`
  margin: 0 5px;
`
const StyledPaymentSubTitle = styled.div<{ isMobile: boolean }>`
  width: ${(props) => (props.isMobile ? '60%' : 'auto')};
`
const TermsAndPrivacyText = styled.div`
  padding-top: 20px;

  a {
    font-weight: 400;
  }
`
